import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useRef } from "react";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import { Alert, Button, Dropdown, Grid, Link, Modal, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import workingStyleSchema from "~/forms/WorkingStyleForm/workingStyleSchema";
import { WorkingStyleBasicsForm, WorkingStyleInsightsForm, WorkingStyleNameForm } from "~/forms/WorkingStyleForm";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "~/components-shared/Card";
import { AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import WorkingStyleForm from "~/forms/WorkingStyleForm/WorkingStyleForm";
import Icon from "~/components-shared/Icon";
import workingStyleSelectors from "~/store/workingStyles/selectors";
import workingStylesActionCreators from "~/store/workingStyles/actions";
import WorkingStyleDeleteModal from "~/components/WorkingStyleDeleteModal";
import moment from "moment";
import useAbacExt from "~/hooks/abac";
import Markdown from "~/components/Markdown";
import InviteWorkingStyleModal from "~/components/InviteWorkingStyleModal";
import WorkingStyleShareModal from "~/components/WorkingStyleShareModal";
import authSelectors from "~/store/auth/selectors";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const StickyAlert = styled(Alert)`
  position: sticky;
  top: 1em;
  z-index: 2;
`;
const Sticky = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}};
  padding: 1rem 5rem;
  margin: 0 -4rem 0 -4rem;

  &:before {
    content: "";
    position: absolute;
    top: -20px;
    left: 0;
    height: 20px;
    width: 100%;
    background: transparent;
    background: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.gradients.primaryVertical;
}};
  }
`;
const WorkingStyleCard = _ref3 => {
  let {
    workingStyle,
    ...rest
  } = _ref3;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const formRef = useRef();
  const dispatch = useDispatch();
  const error = useSelector(workingStyleSelectors.getErrorByWorkingStyle(workingStyle.id));
  const status = useSelector(workingStyleSelectors.getStatusByWorkingStyle(workingStyle.id));
  const user = useSelector(authSelectors.getUser);
  const action = useSelector(workingStyleSelectors.getAction);
  const {
    userHasOneOf
  } = useAbacExt();
  const editing = action === CRUDActionEnum.UPDATE && workingStyle.email === user.email;
  const defaultValues = {
    uniqueId: workingStyle?.uniqueId || "",
    firstName: workingStyle?.firstName || "",
    lastName: workingStyle?.lastName || "",
    bio: workingStyle?.bio || "",
    pronouns: workingStyle?.pronouns || "",
    pronunciation: workingStyle?.pronunciation || "",
    languages: workingStyle?.languages || [],
    location: workingStyle?.location || "",
    birthplace: workingStyle?.birthplace || "",
    timezone: workingStyle?.timezone || "",
    workingHoursFrom: workingStyle?.workingHoursFrom ? `${moment(workingStyle.workingHoursFrom, "hh:mma").isValid() ? moment(workingStyle.workingHoursFrom, "hh:mma").toDate() : ""}` : "",
    workingHoursTo: workingStyle?.workingHoursTo ? `${moment(workingStyle.workingHoursTo, "hh:mma").isValid() ? moment(workingStyle.workingHoursTo, "hh:mma").toDate() : ""}` : "",
    mostProductiveFrom: workingStyle?.mostProductiveFrom ? `${moment(workingStyle.mostProductiveFrom, "hh:mma").isValid() ? moment(workingStyle.mostProductiveFrom, "hh:mma").toDate() : ""}` : "",
    mostProductiveTo: workingStyle?.mostProductiveTo ? `${moment(workingStyle.mostProductiveTo, "hh:mma").isValid() ? moment(workingStyle.mostProductiveTo, "hh:mma").toDate() : undefined}` : "",
    reachTargets: workingStyle?.reachTargets || [],
    hobbies: workingStyle?.hobbies || "",
    reachRestrictions: workingStyle?.reachRestrictions || "",
    insights: workingStyle?.insights || "",
    strengthAreas: workingStyle?.strengthAreas || [" ", " ", " "],
    growthAreas: workingStyle?.growthAreas || [" ", " ", " "],
    mainValues: workingStyle?.mainValues || [" ", " ", " "],
    mainBeliefs: workingStyle?.mainBeliefs || [" ", " ", " "],
    selectedEmoji: workingStyle?.selectedEmoji || "🎈",
    workingStyleAnswers: workingStyle?.workingStyleAnswers || []
  };
  const {
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    setError,
    formState: {
      errors: formErrors
    }
  } = useForm({
    resolver: yupResolver(workingStyleSchema),
    defaultValues
  });

  // Hacky prevent idle timeout
  let timeout;
  useEffect(() => {
    if (formRef) {
      timeout = setInterval(() => {
        formRef.current?.dispatchEvent(new KeyboardEvent("keypress", {
          key: "shift(left)"
        }));
      }, 10 * 60 * 1000);
    } else {
      clearInterval(timeout);
    }
    return () => {
      clearInterval(timeout);
    };
  }, []);
  useEffect(() => {
    if (action) {
      reset(defaultValues);
    }
  }, [action]);
  const handleCancelUpdate = () => {
    dispatch(workingStylesActionCreators.actionChanged(undefined, undefined));
  };
  const handleEdit = () => {
    dispatch(workingStylesActionCreators.actionChanged(CRUDActionEnum.UPDATE, workingStyle.id));
  };
  const handleDelete = () => {
    dispatch(workingStylesActionCreators.actionChanged(CRUDActionEnum.DELETE, workingStyle.id));
  };
  const onSubmit = handleSubmit(data => {
    const modifiedData = data;
    modifiedData.growthAreas = data.growthAreas.filter(item => item.trim() !== "");
    modifiedData.mainBeliefs = data.mainBeliefs.filter(item => item.trim() !== "");
    modifiedData.mainValues = data.mainValues.filter(item => item.trim() !== "");
    modifiedData.strengthAreas = data.strengthAreas.filter(item => item.trim() !== "");
    modifiedData.workingHoursFrom = data.workingHoursFrom ? `${moment(data.workingHoursFrom).isValid() ? moment(data.workingHoursFrom).format("hh:mma") : undefined}` : undefined;
    modifiedData.workingHoursTo = data.workingHoursTo ? `${moment(data.workingHoursTo).isValid() ? moment(data.workingHoursTo).format("hh:mma") : undefined}` : undefined;
    modifiedData.mostProductiveFrom = data.mostProductiveFrom ? `${moment(data.mostProductiveFrom).isValid() ? moment(data.mostProductiveFrom).format("hh:mma") : undefined}` : undefined;
    modifiedData.mostProductiveTo = data.mostProductiveTo ? `${moment(data.mostProductiveTo).isValid() ? moment(data.mostProductiveTo).format("hh:mma") : undefined}` : undefined;
    dispatch(workingStylesActionCreators.updateStarted(workingStyle.id, data));
  });
  const errorMessages = Object.values(formErrors).map(item => Array.isArray(item) ? item.map(_ref4 => {
    let {
      message
    } = _ref4;
    return message;
  }).join(", ") : item.message);
  const errors = error ? [error, ...errorMessages] : errorMessages;
  return /*#__PURE__*/React.createElement(Card, _extends({
    loading: status === StatusStateEnum.LOADING
  }, rest), !!errors.length && /*#__PURE__*/React.createElement(StickyAlert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: errors.join("\n\n")
  })), /*#__PURE__*/React.createElement(Form, {
    ref: formRef,
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Card, {
    s: {
      p: 0,
      mb: 4
    },
    fullWidth: true
  }, !editing && /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    align: "center",
    template: userHasOneOf([AuthoritiesEnum.WORKING_STYLES_WRITE]) ? "1fr auto auto" : "1fr",
    fullWidth: true,
    s: {
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold"
  }, t("WorkingStyleCard.lastEdited"), " ", moment(workingStyle.updatedAt).local().startOf("seconds").fromNow(true), " ", t("Activities.ago")), userHasOneOf([AuthoritiesEnum.WORKING_STYLES_WRITE], workingStyle.email) && /*#__PURE__*/React.createElement(Button, {
    onClick: handleEdit,
    color: "primary",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block"
  }, t("common.actions.update")), userHasOneOf([AuthoritiesEnum.WORKING_STYLES_WRITE], workingStyle.email) && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    })
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.WORKING_STYLES_WRITE], workingStyle.email) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  })), /*#__PURE__*/React.createElement(WorkingStyleNameForm, {
    control: control,
    editing: editing,
    workingStyle: workingStyle,
    watch: watch
  })), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(Card, {
    title: /*#__PURE__*/React.createElement(Text, {
      element: "h2"
    }, t("WorkingStyleCard.basics"), editing && /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: "textSecondary",
      fontSize: 3.5,
      s: {
        ml: 3
      }
    }, t("WorkingStyleCard.basicsDesc"))),
    s: {
      p: 0,
      mb: 12
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(WorkingStyleBasicsForm, {
    control: control,
    watch: watch,
    setValue: setValue,
    setError: setError,
    editing: editing,
    workingStyle: workingStyle
  }))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(Card, {
    title: /*#__PURE__*/React.createElement(Text, {
      element: "h2"
    }, t("WorkingStyleCard.insights"), editing && /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: "textSecondary",
      fontSize: 3.5,
      s: {
        ml: 3
      }
    }, t("WorkingStyleCard.insightsDesc"))),
    s: {
      p: 0,
      mb: 12
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(WorkingStyleInsightsForm, {
    control: control,
    editing: editing,
    workingStyle: workingStyle
  }))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(Card, {
    title: /*#__PURE__*/React.createElement(Text, {
      element: "h2"
    }, t("WorkingStyleCard.workingStyle"), editing && /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: "textSecondary",
      fontSize: 3.5,
      s: {
        ml: 3
      }
    }, t("WorkingStyleCard.workingStyleDesc"))),
    s: {
      p: 0,
      mb: 12
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(WorkingStyleForm, {
    watch: watch,
    setValue: setValue,
    editing: editing,
    workingStyle: workingStyle
  })))), editing && /*#__PURE__*/React.createElement(Sticky, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 5,
    align: "center",
    template: "1fr auto 200px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Link, {
    weight: "semiBold",
    href: window.location.origin + RoutesEnum.WORKING_STYLE_SHORTEN.replace(":workingStyleId", "kenny")
  }, t("WorkingStyleCard.needInspiration"), "?"), /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    onClick: handleCancelUpdate,
    type: "button",
    s: {
      px: 8
    },
    fullWidth: true,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    maxWidth: "230px",
    type: "submit",
    onClick: onSubmit,
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(WorkingStyleDeleteModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.INVITE
  }, /*#__PURE__*/React.createElement(InviteWorkingStyleModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.SHARE
  }, /*#__PURE__*/React.createElement(WorkingStyleShareModal, null)));
};
export default WorkingStyleCard;